import { IWebexIntCallableEntity, IWebexIntContact } from '../../../adapter-interfaces/src';
import { Logger } from '../../../logger/src/Logger';
import { whenWebexReady } from '../services/WebexInstanceService';

/**
 * ===============================
 * Webex Identity Resolver (Universal)
 * ===============================
 * Resolves Webex UUID using:
 *  1️⃣ Email (preferred)
 *  2️⃣ DisplayName + Phone (fallback)
 * Uses caching to avoid repeated lookups.
 * Caches are bounded (max size) to prevent unbounded memory growth in long-running sessions.
 */

/* ------------------------------------------------------------------ */
/*                          CACHES                                     */
/* ------------------------------------------------------------------ */

const MAX_CACHE_SIZE = 500;

function setWithEviction<K, V>(map: Map<K, V>, key: K, value: V, maxSize: number): void {
  if (map.size >= maxSize && !map.has(key)) {
    const firstKey = map.keys().next().value;
    if (firstKey !== undefined) {
      map.delete(firstKey);
    }
  }
  map.set(key, value);
}

const contactIdCache = new Map<string, string>();
const emailCache = new Map<string, string>();
const displayNamePhoneCache = new Map<string, string>();

/* ------------------------------------------------------------------ */
/*                          TYPES                                      */
/* ------------------------------------------------------------------ */

export interface IWebexIdentityInput {
  contactId?: string;
  displayName?: string;
  email?: string;
  phone?: string;
}

/* ------------------------------------------------------------------ */
/*                      HELPER FUNCTIONS                               */
/* ------------------------------------------------------------------ */

function normalizePhone(phone?: string): string {
  if (!phone) return '';
  
  const trimmed = phone.trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  
  return hasPlus ? `+${digits}` : digits;
}

function phonesMatch(p1?: string, p2?: string): boolean {
  if (!p1 || !p2) return false;

  const n1 = normalizePhone(p1);
  const n2 = normalizePhone(p2);

  if (n1 === n2) return true;

  // Suffix match (last 7+ digits)
  if (n1.length >= 7 && n2.endsWith(n1)) return true;
  if (n2.length >= 7 && n1.endsWith(n2)) return true;

  return false;
}

/** True when the string is a UUID v4-shaped id (Webex person ids and Azure AD ids share this shape). */
function isUuidV4Shape(userId: string): boolean {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(userId.trim());
}

function normalizeToUUID(userId: string): string {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  if (uuidRegex.test(userId)) return userId;

  try {
    const decoded = atob(userId);
    const match = decoded.match(
      /([0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})/i
    );
    if (match?.[1]) return match[1];
  } catch {
    // ignore
  }

  return userId;
}

/**
 * `people.list({ displayName })` is picky: "Waseemraj ,Mulla" / "Waseemraj, Mulla" often return 0 items
 * while "Waseemraj Mulla" or "Waseemraj" succeed. Graph also uses "Name (alias)" — Webex search often
 * matches the name without parentheses; token fallbacks must not use "(alias)" as the last name token.
 */
function displayNameCandidatesForPeopleSearch(displayName: string): string[] {
  const raw = displayName.trim();
  if (!raw) return [];

  const out: string[] = [];
  const push = (s: string) => {
    const t = s.replace(/\s+/g, ' ').trim();
    if (t.length < 2) return;
    if (!out.includes(t)) out.push(t);
  };

  const commaFree = raw.replace(/\s*,\s*/g, ' ').replace(/\s+/g, ' ').trim();
  const sansParen = commaFree
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Prefer "Waseemraj Mulla" before "Waseemraj Mulla (wamulla)" for directory search
  push(sansParen);
  if (commaFree !== sansParen) {
    push(commaFree);
  }

  const parts = sansParen.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    push(parts[0]);
    push(parts[parts.length - 1]);
  }

  return out;
}

/* ------------------------------------------------------------------ */
/*                  WEBEX PEOPLE LOOKUPS                               */
/* ------------------------------------------------------------------ */

async function lookupByEmail(email: string): Promise<string | undefined> {
  try {
    await whenWebexReady(5000);
    const webex = (window as any).webex;

    if (!webex?.people) {
      Logger.info('[IdentityResolver] Webex people API not available');
      return undefined;
    }

    const results = await webex.people.list({ email });

    if (results?.items?.length) {
      const person = results.items.find((p: any) =>
        p?.emails?.some(
          (e: string) => e.toLowerCase() === email.toLowerCase()
        )
      ) ?? results.items[0];

      if (person?.id) {
        return normalizeToUUID(person.id);
      }
    }
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    Logger.error(
      `[IdentityResolver] Email lookup failed for ${email}: ${errorMessage}`
    );
  }

  return undefined;
}

function mapWebexPersonToContact(person: Record<string, unknown>, contactId: string): IWebexIntContact {
  const displayName =
    (typeof person.displayName === 'string' && person.displayName) ||
    (typeof person.name === 'string' && person.name) ||
    '';

  const phoneNumbers: IWebexIntCallableEntity[] = [];
  const rawPhones = person.phoneNumbers;
  if (Array.isArray(rawPhones)) {
    rawPhones.forEach((entry: unknown) => {
      if (entry && typeof entry === 'object') {
        const p = entry as { type?: string; value?: string };
        const address = p.value?.trim();
        if (address) {
          phoneNumbers.push({ type: p.type ?? 'work', address });
        }
      } else if (typeof entry === 'string' && entry.trim()) {
        phoneNumbers.push({ type: 'work', address: entry.trim() });
      }
    });
  }
  if (typeof person.mobilePhone === 'string' && person.mobilePhone.trim()) {
    const mobile = person.mobilePhone.trim();
    if (!phoneNumbers.some((p) => p.address === mobile)) {
      phoneNumbers.push({ type: 'mobile', address: mobile });
    }
  }

  const emailAddresses: IWebexIntCallableEntity[] = [];
  const rawEmails = person.emails;
  if (Array.isArray(rawEmails)) {
    rawEmails.forEach((entry: unknown) => {
      const address = typeof entry === 'string' ? entry.trim() : '';
      if (address) {
        emailAddresses.push({ type: 'mail', address });
      }
    });
  }

  const avatarUrl =
    typeof person.avatar === 'string' && person.avatar.trim()
      ? person.avatar.trim()
      : undefined;

  return {
    id: contactId,
    name: displayName,
    displayName,
    phoneNumbers,
    emailAddresses,
    fetchAvatarUrl: avatarUrl ? () => Promise.resolve(avatarUrl) : undefined,
  };
}

/**
 * Loads a Webex directory person by UUID for speed-dial enrichment when Microsoft Graph has no match.
 * Outlook contact ids (AAMk…) and non-UUID ids are ignored.
 */
export async function fetchWebexPersonContactById(
  personId: string
): Promise<IWebexIntContact | undefined> {
  if (typeof personId !== 'string') {
    return undefined;
  }
  const trimmed = personId.trim();
  if (!trimmed || !isUuidV4Shape(trimmed)) {
    return undefined;
  }
  try {
    await whenWebexReady(5000);
    const webex = (window as any).webex;
    if (!webex?.people?.get) {
      return undefined;
    }
    const person = await webex.people.get(trimmed);
    if (!person) {
      return undefined;
    }
    return mapWebexPersonToContact(person as Record<string, unknown>, trimmed);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    Logger.info(
      `[IdentityResolver] fetchWebexPersonContactById: no Webex person for id ${trimmed}: ${msg}`
    );
  }
  return undefined;
}

/**
 * Resolves a stored id that may already be a Webex person id (e.g. DSS directory `contactId`).
 * Returns undefined if the id is not a Webex person (e.g. Graph user id) so callers can fall back to email search.
 */
async function lookupByPersonId(personId: string): Promise<string | undefined> {
  const contact = await fetchWebexPersonContactById(personId);
  if (contact?.id) {
    return normalizeToUUID(contact.id);
  }
  return undefined;
}

async function lookupByDisplayNameAndPhone(
  displayName: string,
  phone: string
): Promise<string | undefined> {
  try {
    await whenWebexReady(5000);
    const webex = (window as any).webex;

    if (!webex?.people) return undefined;

    const candidates = displayNameCandidatesForPeopleSearch(displayName);

    for (const dn of candidates) {
      const results = await webex.people.list({ displayName: dn });

      if (!results?.items?.length) {
        continue;
      }

      const match = results.items.find((person: any) => {
        if (person?.phoneNumbers) {
          return person.phoneNumbers.some((p: any) =>
            phonesMatch(p?.value || p, phone)
          );
        }

        if (person?.mobilePhone) {
          return phonesMatch(person.mobilePhone, phone);
        }

        return false;
      });

      if (match?.id) {
        return normalizeToUUID(match.id);
      }
    }
  } catch (err) {
    Logger.error(
      `[IdentityResolver] DisplayName+Phone lookup failed: ${
        err instanceof Error ? err.message : err
      }`
    );
  }

  return undefined;
}

/* ------------------------------------------------------------------ */
/*                 UNIVERSAL RESOLVER FUNCTION                         */
/* ------------------------------------------------------------------ */

export async function resolveWebexUserId(
  input: IWebexIdentityInput
): Promise<string | undefined> {
  const { contactId, displayName, email, phone } = input;

  /* 1️⃣ contactId cache */
  if (contactId && contactIdCache.has(contactId)) {
    return contactIdCache.get(contactId);
  }

  /* 2️⃣ contactId may already be a Webex person UUID (e.g. DSS directory stored on speed dial) */
  if (contactId) {
    const direct = await lookupByPersonId(contactId);
    if (direct) {
      setWithEviction(contactIdCache, contactId, direct, MAX_CACHE_SIZE);
      return direct;
    }
  }

  /* 3️⃣ Email (preferred method) */
  if (email) {
    const normalizedEmail = email.toLowerCase().trim();

    if (emailCache.has(normalizedEmail)) {
      const cached = emailCache.get(normalizedEmail);
      if (contactId && cached) setWithEviction(contactIdCache, contactId, cached, MAX_CACHE_SIZE);
      return cached;
    }

    const resolved = await lookupByEmail(normalizedEmail);

    if (resolved) {
      setWithEviction(emailCache, normalizedEmail, resolved, MAX_CACHE_SIZE);
      if (contactId) setWithEviction(contactIdCache, contactId, resolved, MAX_CACHE_SIZE);
      return resolved;
    }
  }

  /* 4️⃣ Fallback displayName + phone */
  if (displayName && phone) {
    const cacheKey =
      displayName.toLowerCase().trim() + '|' + normalizePhone(phone);

    if (displayNamePhoneCache.has(cacheKey)) {
      const cached = displayNamePhoneCache.get(cacheKey);
      if (contactId && cached) setWithEviction(contactIdCache, contactId, cached, MAX_CACHE_SIZE);
      return cached;
    }

    const resolved = await lookupByDisplayNameAndPhone(displayName, phone);

    if (resolved) {
      setWithEviction(displayNamePhoneCache, cacheKey, resolved, MAX_CACHE_SIZE);
      if (contactId) setWithEviction(contactIdCache, contactId, resolved, MAX_CACHE_SIZE);
      return resolved;
    }
  }

  Logger.info('[IdentityResolver] Could not resolve Webex user (email and displayName+phone lookup did not find a match)');
  return undefined;
}

/* ------------------------------------------------------------------ */
/*                      CACHE CLEAR (OPTIONAL)                         */
/* ------------------------------------------------------------------ */

export function clearWebexUserIdCache(): void {
  contactIdCache.clear();
  emailCache.clear();
  displayNamePhoneCache.clear();
}
